import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { IApiError } from '@core/interfaces/api-error.interface';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { UserInvitationService } from '@core/services/user-invitation.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ERoles } from '@enums/roles.enum';
import { IUserInvitation } from '@interfaces/user-invitation.interface';
import { ComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { toEventInvitationSource } from '@utilities/event-payload-mapping.util';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface ICreatorInvitationDialogState {
  creatorInvitation: IUserInvitation | TNil;
  loading: boolean;
  error: string | null;
  emailUsedForAnotherCreatorName: string | null;
  emailUsedForAnotherCreatorId: string | null;
  emailUsedInDifferentGroup: boolean;
  cannotInviteSelf: boolean;
}

const initialState: ICreatorInvitationDialogState = {
  cannotInviteSelf: false,
  creatorInvitation: null,
  emailUsedForAnotherCreatorId: null,
  emailUsedForAnotherCreatorName: null,
  emailUsedInDifferentGroup: false,
  error: null,
  loading: false,
};

@Injectable()
export class CreatorInvitationDialogStore extends ComponentStore<ICreatorInvitationDialogState> {
  public readonly creatorInvitation$ = this.selectSignal(
    (state: ICreatorInvitationDialogState) => state.creatorInvitation,
  );
  public readonly loading$ = this.selectSignal((state: ICreatorInvitationDialogState) => state.loading);
  public readonly error$ = this.selectSignal((state: ICreatorInvitationDialogState) => state.error);
  public readonly emailUsedForAnotherCreatorId$ = this.selectSignal(
    (state: ICreatorInvitationDialogState) => state.emailUsedForAnotherCreatorId,
  );
  public readonly emailUsedForAnotherCreatorName$ = this.selectSignal(
    (state: ICreatorInvitationDialogState) => state.emailUsedForAnotherCreatorName,
  );
  public readonly cannotInviteSelf$ = this.selectSignal(
    (state: ICreatorInvitationDialogState) => state.cannotInviteSelf,
  );
  public readonly emailUsedInDifferentGroup$ = this.selectSignal(
    (state: ICreatorInvitationDialogState) => state.emailUsedInDifferentGroup,
  );

  public readonly ALREADY_INVITED_ERROR_CODE = 'ALREADY_INVITED';

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        const errorDetails = this.errorFormatterService.getErrorDetails(error.error as IApiError);
        const errorCode = errorDetails?.[0]?.code;
        const otherCreatorId = errorDetails?.[0]?.params?.['other_creator_id'] as string;
        const otherCreatorName = errorDetails?.[0]?.params?.['other_creator_name'] as string;
        const alreadyAccepted = errorCode === this.ALREADY_INVITED_ERROR_CODE;

        if (alreadyAccepted) {
          this.snackbarService.openError(this.translate.instant('pages.invitation.creatorInvitation.alreadyInvited'));
          this.getInvitation({ id: this.creatorInvitation$()?.id! });
        }

        this.patchState({
          cannotInviteSelf: errorCode === 'SELF_INVITATION_FORBIDDEN',
          emailUsedForAnotherCreatorId: errorCode === 'EMAIL_USED_FOR_DIFFERENT_CREATOR' ? otherCreatorId : null,
          emailUsedForAnotherCreatorName: errorCode === 'EMAIL_USED_FOR_DIFFERENT_CREATOR' ? otherCreatorName : null,
          emailUsedInDifferentGroup: errorCode === 'EMAIL_USED_IN_DIFFERENT_GROUP',
          error: alreadyAccepted ? this.ALREADY_INVITED_ERROR_CODE : errorMessage,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly sendInvitation = this.effect(
    (trigger$: Observable<{ email: string; message: string; creatorId: string }>) => {
      return trigger$.pipe(
        tap(() =>
          this.patchState({
            emailUsedForAnotherCreatorId: null,
            emailUsedForAnotherCreatorName: null,
            error: null,
            loading: true,
          }),
        ),
        switchMap(({ email, message, creatorId }) =>
          this.userInvitationService.sendCreatorInvitation(email, message, creatorId).pipe(
            tap((creatorInvitation: IUserInvitation) => {
              this.patchState({ creatorInvitation, loading: false });
              this.eventTrackingService.track(EEventType.userInvitationSent, {
                creatorId,
                source: toEventInvitationSource(ERoles.creator),
              });
            }),
            this.handleError('Failed to send invitation'),
          ),
        ),
      );
    },
  );

  public readonly revokeInvitation = this.effect((trigger$: Observable<{ id: string }>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(({ id }) => {
        return this.userInvitationService.revokeCreatorInvitation(id).pipe(
          tap(() => {
            this.patchState({ creatorInvitation: null, loading: false });
          }),
          this.handleError('Failed to revoke invitation'),
        );
      }),
    );
  });

  public readonly getInvitation = this.effect((trigger$: Observable<{ id: string }>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(({ id }) => {
        return this.userInvitationService.getCreatorInvitation(id).pipe(
          tap((creatorInvitation: IUserInvitation) => {
            this.patchState({ creatorInvitation, loading: false });
          }),
          this.handleError('Failed to load invitation'),
        );
      }),
    );
  });

  constructor(
    private readonly userInvitationService: UserInvitationService,
    private readonly errorFormatterService: ErrorFormatterService,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService,
    private readonly eventTrackingService: EventTrackingService,
  ) {
    super(initialState);
  }
}
